import { diffArrays } from 'diff';
import { normalizeScriptText } from '../script-text';
import type { VersionFormat } from './colors';

/**
 * The changed pages of a collated copy with no per-page marks, worked out by comparing its text with the last
 * collated version's. Deterministic and conservative: any difference on a page marks it, a reflowed page is a
 * changed page, and there are no thresholds and no positional header or footer rules. The sandbox's `revisions diff`
 * and the `revisions-valid` exit check both call `diffChangedPages` on the same files. Everything it answers is page
 * and version numbers: the text never leaves it.
 */

const PAGE_MARKER = /^==== page (\d+) ====$/;
const PAGE_NUMBER = /^(?:\(\d+[A-Z]{0,2}\)|\d+[A-Z]{0,2})\.?$/;
const MONTH =
  '(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)';
/** Dates as a header prints them: `10/01/26`, `11.03.2026`, `2026-11-05`, `November 5, 2026`, `5 Nov 2026`. */
const DATES = [
  /\(?\b\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}\b\)?/g,
  /\(?\b\d{4}-\d{1,2}-\d{1,2}\b\)?/g,
  new RegExp(`\\(?\\b${MONTH}\\.?\\s+\\d{1,2}(?:st|nd|rd|th)?,?\\s+\\d{4}\\b\\)?`, 'gi'),
  new RegExp(`\\(?\\b\\d{1,2}(?:st|nd|rd|th)?\\s+${MONTH}\\.?,?\\s+\\d{4}\\b\\)?`, 'gi'),
];

/**
 * A line in the form two versions compare in, or null when it carries nothing to compare: the shared script-text
 * normal form (tags, NFKC, quotes, dashes, whitespace), one leading and one trailing `*` dropped with their spaces,
 * dates dropped (a header's draft date changes on every page of a new copy); null for an empty result, a page
 * marker, or a line that is only a page number (`12.`, `12A`, `(12)`). A line reading `OMITTED` is kept.
 */
export function normalizeScriptLine(line: string): string | null {
  let text = normalizeScriptText(line);
  if (PAGE_MARKER.test(text)) return null;
  text = text.replace(/^\*\s*/, '').replace(/\s*\*$/, '');
  for (const date of DATES) text = text.replace(date, ' ');
  text = text.replace(/\s+/g, ' ').trim();
  if (text.length === 0 || PAGE_NUMBER.test(text)) return null;
  return text;
}

export type ScriptPage = { page: number; lines: string[] };

/** A script.txt split into its pages on the `==== page N ====` markers, each line normalised, nulls dropped. */
export function pagesOf(scriptText: string): ScriptPage[] {
  const pages: ScriptPage[] = [];
  for (const raw of scriptText.split(/\r?\n/)) {
    const marker = PAGE_MARKER.exec(raw.trim());
    if (marker) {
      pages.push({ page: Number(marker[1]), lines: [] });
      continue;
    }
    const line = normalizeScriptLine(raw);
    const current = pages.at(-1);
    if (line !== null && current) current.lines.push(line);
  }
  return pages;
}

/** A page's lines as one string, for comparing whole pages (a normalised line never holds a newline). */
function pageKey(page: ScriptPage): string {
  return page.lines.join('\n');
}

/**
 * The current version's pages (layout numbers, in order) split into changed and unchanged against `base`, with any
 * changed page whose lines equal a page of an `interim` text taken out (that content was analysed when it came in).
 * A longest-common-subsequence diff runs over the two versions' pages in order, a page matching only when its lines
 * equal, line for line, a page of the base: so a page with an inserted, replaced or deleted line is changed, and so
 * is a page that text was pushed onto across a page break. Whole pages rather than single lines are the diff's
 * unit because the lines every page repeats (a header, `CONTINUED:`) would otherwise let the alignment slip by a
 * page around an inserted A-page. A page with no lines at all is unchanged.
 */
export function diffChangedPages({
  current,
  base,
  interim,
}: {
  current: string;
  base: string;
  interim: string[];
}): { changed: number[]; unchanged: number[] } {
  const now = pagesOf(current);
  const withText = now.filter((p) => p.lines.length > 0);
  const before = pagesOf(base).filter((p) => p.lines.length > 0);

  const same = new Set<number>();
  let at = 0;
  for (const change of diffArrays(before.map(pageKey), withText.map(pageKey))) {
    if (change.removed) continue;
    if (!change.added)
      for (let k = 0; k < change.count; k++) same.add((withText[at + k] as ScriptPage).page);
    at += change.count;
  }

  const carried = new Set(interim.flatMap(pagesOf).map(pageKey));
  const changed: number[] = [];
  const unchanged: number[] = [];
  for (const page of now) {
    const kept = page.lines.length === 0 || same.has(page.page) || carried.has(pageKey(page));
    (kept ? unchanged : changed).push(page.page);
  }
  return { changed, unchanged };
}

/**
 * Which earlier versions a diff compares against: the highest-numbered collated version (whatever its scheme;
 * version 1 at worst) and the `pages` versions after it, in order. Throws `no_collated_base` when no earlier
 * version is collated.
 */
export function diffBaseFor(earlier: { version: number; format: VersionFormat | null }[]): {
  base: number;
  interim: number[];
} {
  const collated = earlier.filter((v) => v.format === 'collated').map((v) => v.version);
  if (collated.length === 0) throw new Error('no_collated_base');
  const base = Math.max(...collated);
  const interim = earlier
    .filter((v) => v.format === 'pages' && v.version > base)
    .map((v) => v.version)
    .sort((x, y) => x - y);
  return { base, interim };
}
